import { makeProviderStatementApi, type ProviderStatementApi } from '@/api/services/providerStatements';
import type { StatementViewModel } from '@/api/types/providerStatements';

// One config object per provider, driving the single ProviderStatementPage. Mirrors
// the providerFunds config registry. The 9 providers collapse to TWO statement
// families (CML bank debit/credit, EcommPay card single-amount); they differ only by
// the column preset, the statement-id/sort field, the route base, the title, and
// whether API sync is supported.

export type ProviderFamily = 'cml' | 'ecommpay';

export type ProviderKey =
  | 'ecommPayUsd'
  | 'ecommPayEur'
  | 'ecommPayGbp'
  | 'cmlChf'
  | 'cmlGbp'
  | 'cmlEur'
  | 'cmlUsd'
  | 'cmlSek'
  | 'cmlDkk';

// All gates live under the MatchingEngine permission group (+ EditProvider on
// MoneyTransactions for the bulk edit-provider action).
export const PS_PERMS = {
  page: 'User.SafeguardingReconciliation.MatchingEngine.ApmProviders',
  match: 'User.SafeguardingReconciliation.MatchingEngine.Match',
  unmatch: 'User.SafeguardingReconciliation.MatchingEngine.Unmatch',
  delete: 'User.SafeguardingReconciliation.MatchingEngine.Delete',
  uploadExcel: 'User.SafeguardingReconciliation.MatchingEngine.UploadExcel',
  manualSync: 'User.SafeguardingReconciliation.MatchingEngine.ManualSync',
  editProvider: 'User.SafeguardingReconciliation.MoneyTransactions.EditProvider',
} as const;

export interface ProviderStatementConfig {
  key: ProviderKey;
  routeBase: string; // kebab of the concrete AppService class, minus AppService
  title: string; // ProviderTitle (used verbatim in header + download filenames)
  family: ProviderFamily;
  supportsApiSync: boolean;
  statementIdField: string; // the id used for statement selection + delete
  statementSortField: string; // the server-side default sort column
  api: ProviderStatementApi<StatementViewModel>;
}

function cml(key: ProviderKey, routeBase: string, title: string): ProviderStatementConfig {
  return {
    key,
    routeBase,
    title,
    family: 'cml',
    supportsApiSync: false,
    statementIdField: 'generatedId',
    statementSortField: 'date',
    api: makeProviderStatementApi(routeBase, false),
  };
}

function ecommPay(key: ProviderKey, routeBase: string, title: string): ProviderStatementConfig {
  return {
    key,
    routeBase,
    title,
    family: 'ecommpay',
    supportsApiSync: true,
    statementIdField: 'transactionId',
    statementSortField: 'operationCompletedAt',
    api: makeProviderStatementApi(routeBase, true),
  };
}

export const PROVIDER_STATEMENT_REGISTRY: Record<ProviderKey, ProviderStatementConfig> = {
  ecommPayUsd: ecommPay('ecommPayUsd', 'ecomm-pay-usd-statement', 'EcommPay - USD'),
  ecommPayEur: ecommPay('ecommPayEur', 'ecomm-pay-eur-statement', 'EcommPay - EUR'),
  ecommPayGbp: ecommPay('ecommPayGbp', 'ecomm-pay-gbp-statement', 'EcommPay - GBP'),
  cmlChf: cml('cmlChf', 'cml-chf-statement', 'Lunaro FSL CHF Client Seg CFD'),
  cmlGbp: cml('cmlGbp', 'cml-gbp-statement', 'Lunaro FSL GBP Client Seg SB'),
  cmlEur: cml('cmlEur', 'cml-eur-statement', 'Lunaro FSL EUR Client Seg SB'),
  cmlUsd: cml('cmlUsd', 'cml-usd-statement', 'Lunaro FSL USD Client Seg'),
  cmlSek: cml('cmlSek', 'cml-sek-statement', 'Lunaro FSL SEK Client Seg CFD'),
  cmlDkk: cml('cmlDkk', 'cml-dkk-statement', 'Lunaro FSL DKK Client Seg'),
};
